/**
 * Az AVSlack jele: két beszédbuborék egymásnak dőlve.
 *
 * A huddle-t itt ölelésnek hívjuk, és az egész projekt fő oka az, hogy ez
 * működjön - a jel ezt mondja ki. A két buborék között futó rés nem díszítés:
 * attól látszik két alaknak, ami különben egyetlen folttá olvadna.
 *
 * ─── Miért itt, kódban, és nem képfájlként? ─────────────────────────────────
 *
 * Mert így KÖVETI A TÉMÁT. Az elülső buborék `currentColor`, a hátsó az
 * `--alert` token: sötét témában mindkettő magától a világosabb változatra
 * vált, ahogy a felület többi eleme is. Egy PNG-nek ehhez két példány kellene,
 * és a kettő előbb-utóbb elcsúszna egymástól.
 *
 * A csempés változat (`LogoTile`) a saját színeit hozza - az az alkalmazás-
 * ikon, annak minden háttéren ugyanúgy kell kinéznie.
 *
 * A forrás és a többi méret: `design/logo/`.
 */

import { APP_ICONS, DEFAULT_APP_ICON, type AppIcon } from '@avslack/shared';

/*
  A maszkok azonosítója modulszintű állandó, nem példányonként generált.

  Ha a jel többször is a lapon van, az azonosítók ütköznek, és mindegyik
  példány az elsőt használja - de a maszkok tartalma szóról szóra ugyanaz,
  úgyhogy ez nem látszik. Egy körülményesebb, példányonkénti azonosító semmit
  nem oldana meg, csak zajt vinne a kimenetbe.
*/
const RES_MASK = 'avslack-logo-res';
const RES_MASK_TILE = 'avslack-logo-res-tile';

/**
 * A csupasz jel. A szülő szövegszínét veszi fel, ezért `text-accent` (vagy
 * bármi más) osztállyal színezhető.
 */
export function Logo({
  className = '',
  title = 'AVSlack',
}: {
  className?: string;
  title?: string;
}) {
  return (
    <svg viewBox="0 0 512 512" className={className} role="img" aria-label={title}>
      <mask id={RES_MASK}>
        <rect width="512" height="512" fill="#fff" />
        <g
          transform="translate(76 112) rotate(-8 107 93)"
          fill="#000"
          stroke="#000"
          strokeWidth="42"
          strokeLinejoin="round"
        >
          <rect width="214" height="186" rx="52" />
          <path d="M70 170 L126 170 L60 214 Z" />
        </g>
      </mask>

      <g mask={`url(#${RES_MASK})`}>
        <g
          transform="translate(232 196) rotate(8 107 93)"
          fill="var(--alert)"
          stroke="var(--alert)"
          strokeWidth="18"
          strokeLinejoin="round"
        >
          <rect width="214" height="186" rx="52" strokeWidth="0" />
          <path d="M88 170 L144 170 L154 214 Z" />
        </g>
      </g>

      <g
        transform="translate(76 112) rotate(-8 107 93)"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="18"
        strokeLinejoin="round"
      >
        <rect width="214" height="186" rx="52" strokeWidth="0" />
        <path d="M70 170 L126 170 L60 214 Z" />
      </g>
    </svg>
  );
}

/**
 * Az alkalmazás-ikon: színes csempe, rajta a jel.
 *
 * Ugyanaz, amit a tálcán és a böngészőfülön lát az ember - a bejelentkező
 * képernyőn ezért ez áll, nem a csupasz jel.
 *
 * A NÉGY SZÍN a `APP_ICONS` táblából jön, nem ide van írva. Ugyanabból a
 * táblából dolgozik a PNG-generáló szkript és a manifest is: ha a csempe
 * színe itt külön állna, előbb-utóbb elcsúszna a tálcaikontól - pont attól,
 * amit ennek meg kellene mutatnia.
 */
export function LogoTile({
  className = '',
  title = 'AVSlack',
  icon = DEFAULT_APP_ICON,
}: {
  className?: string;
  title?: string;
  icon?: AppIcon;
}) {
  const palette = APP_ICONS[icon];

  /*
    A GRADIENS AZONOSÍTÓJA PALETTÁNKÉNT KÜLÖN - eltérően a maszkoktól.

    A maszkok tartalma minden palettánál szóról szóra ugyanaz, ezért ott az
    ütközés nem látszik. A gradiens viszont épp a színt hordozza: a
    beállításokban mind a hat csempe egyszerre van a lapon, és közös
    azonosítóval mind a hat az elsőnek a színét venné fel.
  */
  const gradientId = `avslack-logo-tile-${icon}`;

  return (
    <svg viewBox="0 0 512 512" className={className} role="img" aria-label={title}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={palette.tileTop} />
          <stop offset="1" stopColor={palette.tileBottom} />
        </linearGradient>
        <mask id={RES_MASK_TILE}>
          <rect width="512" height="512" fill="#fff" />
          <g
            transform="translate(76 112) rotate(-8 107 93)"
            fill="#000"
            stroke="#000"
            strokeWidth="42"
            strokeLinejoin="round"
          >
            <rect width="214" height="186" rx="52" />
            <path d="M70 170 L126 170 L60 214 Z" />
          </g>
        </mask>
      </defs>

      <rect width="512" height="512" rx="116" fill={`url(#${gradientId})`} />

      <g transform="translate(256 256) scale(0.9) translate(-261 -262)">
        <g mask={`url(#${RES_MASK_TILE})`}>
          <g
            transform="translate(232 196) rotate(8 107 93)"
            fill={palette.back}
            stroke={palette.back}
            strokeWidth="18"
            strokeLinejoin="round"
          >
            <rect width="214" height="186" rx="52" strokeWidth="0" />
            <path d="M88 170 L144 170 L154 214 Z" />
          </g>
        </g>
        <g
          transform="translate(76 112) rotate(-8 107 93)"
          fill={palette.front}
          stroke={palette.front}
          strokeWidth="18"
          strokeLinejoin="round"
        >
          <rect width="214" height="186" rx="52" strokeWidth="0" />
          <path d="M70 170 L126 170 L60 214 Z" />
        </g>
      </g>
    </svg>
  );
}
